import urlRegexSafe from 'url-regex-safe';

import { randomUuid } from '../helpers';
import profaneWordsList from './eng.json';
// The composer types list lines through the same grammar, so what it writes
// is always what this renders. See ./formatting.ts.
import { INDENT_COLUMNS, LIST_ITEM } from './formatting';

const urlRegex = urlRegexSafe();
const mentionRegex = /@[a-zA-Z0-9_.]+/g;
const spaceRegex = /s\/[a-zA-Z0-9_.-]+/g;
const factiiiRegex = /f\/[a-zA-Z0-9_.-]+/g;
// A tag only starts where nothing runs into it from the left, so
// "resources/time" keeps its `s/time` as text and "user@host" is not a
// mention. Every other character — a space, an opening bracket, a quote —
// still lets a tag start.
const RUNS_INTO_TAG = /[\w/]/;

// Markdown code — fenced blocks, then inline spans.
const FENCED_CODE = /```[a-zA-Z0-9+#-]*\n?[\s\S]*?```/g;
const INLINE_CODE = /`[^`\n]+`/g;
// A fenced block with the line break on either side of its fence, for chat.
const CHAT_FENCED_CODE = new RegExp(`\\n?${FENCED_CODE.source}\\n?`, 'g');
const BOLD = /\*\*([^*\n]+)\*\*/g;
// Italic and strike require a non-space right inside the delimiters, so
// "2 * 3 * 4" stays arithmetic and "a ~~ b" stays punctuation. Bold predates
// this rule and keeps its looser form so already-published posts don't shift.
const ITALIC_STAR = /(^|[^*\w])\*([^\s*][^*\n]*[^\s*]|[^\s*])\*/g;
// Underscore italics need word boundaries either side so snake_case survives.
const ITALIC_UNDERSCORE = /(^|[^_\w])_([^\s_][^_\n]*[^\s_]|[^\s_])_(?!\w)/g;
const STRIKE = /~~([^\s~][^~\n]*[^\s~]|[^\s~])~~/g;
// Only http(s) — the scheme allow-list is what keeps `javascript:` out before
// the sanitizer ever sees it. The optional <url> wrapper is for content saved
// before shouldSkipUrlAtPosition learned to leave a link target alone.
const MD_LINK =
  /\[([^\]\n]+)\]\((?:<url[^>]*>)?(https?:\/\/[^\s)<]+)(?:<\/url>)?\)/g;
// A CommonMark autolink, `<https://…>`. The web composer's markdown serializer
// wrote every link whose text is its own URL this way until it learned to
// write the bare URL. To the HTML sanitiser one is an unknown tag, so the link
// vanished from every renderer; rows saved before the fix still hold them.
const AUTOLINK = /<(https?:\/\/[^>\s]+)>/g;
// `#` needs a space after it, so a #hashtag is not a heading.
const HEADING = /^(#{1,3})[ \t]+(.*)$/;
const QUOTE = /^[ \t]*>[ \t]?(.*)$/;
// Private-use characters, so a placeholder can never collide with typed text
// and never matches the url/mention/space/factiii/profanity patterns.
const SHIELD_OPEN = '\uE000';
const SHIELD_CLOSE = '\uE001';
const SHIELD = /\uE000(\d+)\uE001/g;
// A second, separate shield for whole entity tags. Inline marks must not see
// inside one: `<url id=1>a_b_c</url>` is a link, not italics.
const TAG_SHIELD_OPEN = '\uE002';
const TAG_SHIELD_CLOSE = '\uE003';
const TAG_SHIELD = /\uE002(\d+)\uE003/g;
const ENTITY_TAG = /<(url|mention|s|f|c|a)\b[^>]*>[\s\S]*?<\/\1>/g;
// An opening or closing tag exactly as `contentFilter` writes it at save time:
// `<url id=4>`, `<f factiiiId=12 id=4>`, `</url>`. Chat content is typed text
// plus these, so in a message nothing else is a tag (see `chat` on
// tagMarkdownForRender). Each of them renders through a fixed renderer that
// takes only its text, so one typed by hand can do no more than a real one.
const WRITTEN_TAG =
  /<(?:url|mention|s|c) id=[\w-]>|<f factiiiId=\d+ id=[\w-]>|<\/(?:url|mention|s|f|c)>/g;
// The markup the system bot writes into its rooms (sendBotGroupMessage):
// `<strong>Label:</strong> value<br/>`, around values that people typed. Bare,
// with no attribute, these three can carry nothing, so they stay tags in a
// message whoever wrote them, and everything around them is still escaped.
const INERT_TAG = /<\/?(?:b|strong)>|<br ?\/>/g;
const KEPT_IN_CHAT = new RegExp(
  `${WRITTEN_TAG.source}|${INERT_TAG.source}`,
  'g'
);

/**
 * Lift code spans out of `text` so nothing inside them gets auto-tagged: a
 * backticked `Some.Thing` is a code sample, not a hostname, and `@default` is
 * not a mention. Restored verbatim by `unshieldCode`, so the backticks survive
 * into storage and the edit round-trip.
 */
function shieldCode(text: string): { shielded: string; blocks: string[] } {
  const blocks: string[] = [];
  const stash = (match: string) => {
    blocks.push(match);
    return `${SHIELD_OPEN}${blocks.length - 1}${SHIELD_CLOSE}`;
  };
  const shielded = text.replace(FENCED_CODE, stash).replace(INLINE_CODE, stash);
  return { shielded, blocks };
}

function unshieldCode(text: string, blocks: string[]): string {
  return text.replace(SHIELD, (_match, i: string) => blocks[Number(i)] ?? '');
}

/**
 * Lift whole entity tags out of `text` so the inline mark rules cannot reach
 * inside one. Without this, `<url id=1>host/a_b_c</url>` picks up italics and
 * `<c id=1>***</c>` picks up bold.
 *
 * The tag goes back verbatim, and because a shielded tag is a single token a
 * mark can still wrap one: `**see <url ...>x</url>**` still bolds the link.
 */
function shieldTags(text: string): { shielded: string; tags: string[] } {
  const tags: string[] = [];
  const shielded = text.replace(ENTITY_TAG, (match) => {
    tags.push(match);
    return `${TAG_SHIELD_OPEN}${tags.length - 1}${TAG_SHIELD_CLOSE}`;
  });
  return { shielded, tags };
}

function unshieldTags(text: string, tags: string[]): string {
  return text.replace(TAG_SHIELD, (_match, i: string) => tags[Number(i)] ?? '');
}

function startsTag(text: string, offset: number): boolean {
  return offset === 0 || !RUNS_INTO_TAG.test(text[offset - 1] ?? '');
}

function isInsideTag(text: string, index: number, tag: string): boolean {
  const before = text.slice(0, index);
  const open = before.lastIndexOf(`<${tag} `);
  const close = before.lastIndexOf(`</${tag}>`);
  return open > close;
}

function findUrlsToSkip(text: string): Set<string> {
  const urlsToSkip = new Set<string>();

  // Find all URLs in the text
  const urlMatches = Array.from(
    text.matchAll(new RegExp(urlRegex.source, 'gi'))
  );

  for (const match of urlMatches) {
    const url = match[0];
    const index = match.index;
    if (index === undefined) continue;

    // Check if this URL should be skipped
    if (shouldSkipUrlAtPosition(text, index, url)) {
      urlsToSkip.add(url);
    }
  }

  return urlsToSkip;
}

function shouldSkipUrlAtPosition(
  text: string,
  index: number,
  url: string
): boolean {
  const before = text.slice(0, index);
  const after = text.slice(index + url.length);

  // A markdown link's target — `[text](https://…)`. Leave it for
  // tagMarkdownForRender, which turns the whole thing into one anchor. Tagging
  // it here would bury the URL in a <url> tag the link rule can no longer see.
  if (/\]\($/.test(before) && /^[^\s)]*\)/.test(after)) return true;

  // Check if URL is inside ANY HTML attribute (href, src, action, etc.)
  // Look for pattern: attribute="URL" or attribute='URL'
  const attributeDoubleQuoteMatch = before.match(/\w+\s*=\s*"[^"]*$/);
  const attributeSingleQuoteMatch = before.match(/\w+\s*=\s*'[^']*$/);

  if (attributeDoubleQuoteMatch || attributeSingleQuoteMatch) {
    const quote = attributeDoubleQuoteMatch ? '"' : "'";
    const nextQuote = after.indexOf(quote);
    if (nextQuote !== -1) {
      return true; // Skip URLs in any HTML attribute
    }
  }

  // Check if URL is inside anchor tag content
  const lastAOpen = Math.max(
    before.lastIndexOf('<a '),
    before.lastIndexOf('<a>')
  );
  const lastAClose = before.lastIndexOf('</a>');

  if (lastAOpen > lastAClose) {
    const nextAClose = after.indexOf('</a>');
    if (nextAClose !== -1) {
      return true; // Skip URLs inside anchor tag content
    }
  }

  // Check if URL is inside any other tag content that should be preserved
  // Look for pattern: <tag>URL or <tag attr="value">URL
  const lastTagOpen = before.lastIndexOf('<');
  const lastTagClose = before.lastIndexOf('>');

  if (lastTagOpen > lastTagClose) {
    // We're inside a tag definition (between < and >)
    return true; // Skip URLs inside tag definitions
  }

  return false;
}

/**
 * `<https://…>` autolinks as the bare URL they hold. Code spans are left as
 * typed. For every path that turns stored content back into text: the edit
 * box, a notification, a preview.
 */
export const unwrapAutolinks = (content: string): string => {
  if (!content || !content.includes('<http')) return content;
  const { shielded, blocks } = shieldCode(content);
  return unshieldCode(shielded.replace(AUTOLINK, '$1'), blocks);
};

export const contentFilter = async (
  content: string,
  postId?: string,
  getFactiii?: (match: string) => Promise<{ id: number } | null>
) => {
  const id = postId ?? randomUuid();

  // Code spans are lifted out before anything is tagged, so a backticked
  // `Some.Thing` never becomes a link. They go back in untouched at the end.
  // An autolink `<https://…>` is its bare URL, so it is tagged like one rather
  // than skipped as "inside a tag" and stored where no renderer shows it.
  const { shielded: codeShielded, blocks } = shieldCode(content);
  const shielded = codeShielded.replace(AUTOLINK, '$1');

  // First, identify URLs that should be skipped
  const urlsToSkip = findUrlsToSkip(shielded);

  const replaceWord = (match: string, tag: string, offset: number) => {
    if (isInsideTag(shielded, offset, tag)) {
      return match;
    }

    // Special handling for URLs - skip if in our skip set
    if (tag === 'url' && urlsToSkip.has(match)) {
      return match;
    }

    return `<${tag} id=${id[0]}>${match}</${tag}>`;
  };

  const replaceFactiii = async (match: string) => {
    const factiii = await getFactiii?.(match.slice(2));
    if (!factiii) return match;
    return `<f factiiiId=${factiii.id} id=${id[0]}>${match}</f>`;
  };

  const formatContent = async (text: string) => {
    const tokens = text.split(
      new RegExp(
        `(${urlRegex.source}|${spaceRegex.source}|${mentionRegex.source}|${factiiiRegex.source})`,
        'gi'
      )
    );

    const replacedTokens = await Promise.all(
      tokens.map(async (token, index) => {
        if (!token) return '';

        const offset = tokens.slice(0, index).join('').length;

        if (token.match(urlRegex)) {
          return replaceWord(token, 'url', offset);
        } else if (!startsTag(text, offset)) {
          // Mid-word, so the delimiter is punctuation the author typed, not a
          // tag. Falls through to the cuss word filter like any plain text.
          return cussWordFilter(token, id);
        } else if (token.match(mentionRegex)) {
          return replaceWord(token, 'mention', offset);
        } else if (token.match(spaceRegex)) {
          return replaceWord(token, 's', offset);
        } else if (token.match(factiiiRegex)) {
          return await replaceFactiii(token);
        } else {
          // Now apply cuss word filter
          return cussWordFilter(token, id);
        }
      })
    );

    return replacedTokens.join('');
  };

  return {
    filteredContent: unshieldCode(await formatContent(shielded), blocks),
    id
  };
};

/**
 * Wrap bare URLs in `<url>` tags at render time, leaving any tag the write-time
 * `contentFilter` already produced untouched.
 *
 * Write-time tagging only covers content saved after that pipeline landed, and
 * never covers a message the sender is looking at optimistically before the
 * server echo arrives. Both cases render as plain text without this. Unlike
 * `contentFilter` this is synchronous and URL-only, so it is safe to call
 * during render.
 */
export const tagUrlsForRender = (content: string, id: string): string => {
  if (!content) return content;

  // Same shielding as the write path, so a code sample is not linkified on the
  // way to the screen either.
  const { shielded, blocks } = shieldCode(content);
  const urlsToSkip = findUrlsToSkip(shielded);
  const tokens = shielded.split(new RegExp(`(${urlRegex.source})`, 'gi'));

  let offset = 0;
  const tagged = tokens
    .map((token) => {
      const start = offset;
      offset += token?.length ?? 0;

      if (!token) return '';
      // Only whole-token matches — a capture-group split can hand back a
      // non-URL token that merely contains something URL-shaped.
      if (token.match(urlRegex)?.[0] !== token) return token;
      if (urlsToSkip.has(token)) return token;
      // Already wrapped by the write-time filter.
      if (isInsideTag(shielded, start, 'url')) return token;

      return `<url id=${id[0]}>${token}</url>`;
    })
    .join('');

  return unshieldCode(tagged, blocks);
};

type ListItem = {
  depth: number;
  ordered: boolean;
  body: string;
  /** The number an ordered item was typed with. */
  number: number;
};
/** A source line, or a block of them that has collapsed into one element. */
type Piece = { text: string; isBlock: boolean };

/**
 * Turn the inline marks into tags: bold, italic, strikethrough and `[text](url)`
 * links. Runs on text whose code spans and entity tags are already shielded, so
 * none of these rules can reach inside a code sample or a `<mention>`.
 *
 * Strike is `<del>`, never `<s>` — `<s>` is the space-link tag this file writes
 * at save time, and the two would be indistinguishable to the renderer.
 */
function tagInlineMarks(text: string, id: string): string {
  return (
    text
      // Bold first: the single-asterisk rule would otherwise eat the inner pair
      // of a `**bold**` and leave stray asterisks behind.
      .replace(BOLD, `<b id=${id[0]}>$1</b>`)
      .replace(STRIKE, `<del id=${id[0]}>$1</del>`)
      .replace(ITALIC_STAR, `$1<i id=${id[0]}>$2</i>`)
      .replace(ITALIC_UNDERSCORE, `$1<i id=${id[0]}>$2</i>`)
  );
}

/**
 * Turn line-led markdown into block tags: `# ` headings, `> ` quotes, and runs
 * of `- ` / `1. ` list items.
 *
 * Both renderers already style `h1`-`h3`, `blockquote`, `ul`, `ol` and `li`, so
 * emitting the tags is all it takes to get Slack- and Trello-shaped formatting
 * on web and mobile from the plain text the author typed. List nesting is
 * relative: any deeper indent than the item above steps in one level, so the
 * one-space and two-space indents people actually type both nest.
 *
 * The newlines on either side of a block are dropped, because these are block
 * elements with their own margins — keeping them would show as a blank line on
 * web, where the renderer sets `white-space: pre-wrap`.
 */
function tagBlocksForRender(text: string, id: string, chat: boolean): string {
  const lines = text.split('\n');
  const pieces: Piece[] = [];

  const indentColumns = (whitespace: string): number =>
    whitespace.replace(/\t/g, ' '.repeat(INDENT_COLUMNS)).length;

  let index = 0;
  while (index < lines.length) {
    const line = lines[index] ?? '';

    const heading = line.match(HEADING);
    if (heading && chat) {
      // A chat line never turns into page-sized text: a heading is a bold
      // line. It is inline, so it keeps the line breaks around it.
      pieces.push({
        text: `<b id=${id[0]}>${heading[2] ?? ''}</b>`,
        isBlock: false
      });
      index += 1;
      continue;
    }
    if (heading) {
      const level = (heading[1] ?? '#').length;
      pieces.push({
        text: `<h${level} id=${id[0]}>${heading[2] ?? ''}</h${level}>`,
        isBlock: true
      });
      index += 1;
      continue;
    }

    if (QUOTE.test(line)) {
      // Consecutive `>` lines are one quote. `<br/>` rather than a newline
      // because only web renders the raw newline — mobile collapses it.
      const quoted: string[] = [];
      for (; index < lines.length; index += 1) {
        const quote = (lines[index] ?? '').match(QUOTE);
        if (!quote) break;
        quoted.push(quote[1] ?? '');
      }
      pieces.push({
        text: `<blockquote id=${id[0]}>${quoted.join('<br/>')}</blockquote>`,
        isBlock: true
      });
      continue;
    }

    // In chat `+ 1 from me` is a sentence, so `+` does not start a list
    // there. `-` and `*` do, as in every chat app.
    const startsList = (candidate: string): RegExpMatchArray | null => {
      const item = candidate.match(LIST_ITEM);
      return item && !(chat && item[2] === '+') ? item : null;
    };

    if (!startsList(line)) {
      pieces.push({ text: line, isBlock: false });
      index += 1;
      continue;
    }

    // Collect the whole run of item lines. Any other text ends the list, which
    // is also how ending one by hand works. Blank lines between items do not:
    // a "loose" list (the CommonMark shape the rich editor and pasted markdown
    // write) stays one list, so a sub-list after a blank line still nests.
    // `widths` holds the indent that opened each level, so an item's depth is
    // read off the item above it rather than off a fixed step size.
    const items: ListItem[] = [];
    const widths: number[] = [];
    for (; index < lines.length; index += 1) {
      // Bounded by the text: past its last line `lines[next]` is undefined,
      // which reads as blank, so an unbounded skip after a list that ends the
      // text never stopped — and froze every page rendering that post.
      let next = index;
      while (
        items.length > 0 &&
        next < lines.length &&
        (lines[next] ?? '').trim() === ''
      )
        next += 1;
      const item = startsList(lines[next] ?? '');
      if (!item) break;
      index = next;
      const width = indentColumns(item[1] ?? '');
      while (widths.length > 0 && width < (widths[widths.length - 1] ?? 0)) {
        widths.pop();
      }
      if (widths.length === 0 || width > (widths[widths.length - 1] ?? 0)) {
        widths.push(width);
      }
      items.push({
        depth: widths.length - 1,
        ordered: /\d/.test(item[2] ?? ''),
        body: item[3] ?? '',
        number: parseInt(item[2] ?? '', 10)
      });
    }

    pieces.push({ text: buildList(items), isBlock: true });
  }

  // Drop the separator next to a block, so it sits flush against the text
  // around it rather than adding a blank line on top of its own margin.
  return pieces
    .map((piece, i) => {
      const previous = pieces[i - 1];
      const separator =
        i > 0 && !previous?.isBlock && !piece.isBlock ? '\n' : '';
      return separator + piece.text;
    })
    .join('');
}

/** Nest a flat run of list items by depth into `<ul>`/`<ol>` markup. */
function buildList(items: ListItem[]): string {
  // Open list tags, outermost first. An item at depth d belongs in open[d], so
  // the stack is exactly one longer than the depth it is rendering.
  const open: ('ul' | 'ol')[] = [];
  let html = '';

  for (const item of items) {
    // Depths arrive stepping in one at a time; the clamp keeps a malformed run
    // from opening a level with no parent item to hang it on.
    const depth = Math.min(item.depth, open.length);
    const tag = item.ordered ? 'ol' : 'ul';

    while (open.length > depth + 1) html += `</li></${open.pop() ?? 'ul'}>`;
    // Switching marker at the same depth starts a new list, so typing `1.`
    // under a `-` gives numbers rather than another bullet.
    if (open.length === depth + 1 && open[depth] !== tag) {
      html += `</li></${open.pop() ?? 'ul'}>`;
    }

    if (open.length === depth + 1) {
      html += '</li>';
    } else {
      // A list numbers from what its first item was typed with, as in
      // CommonMark: a lone `3. third point` must not read `1. third point`.
      html +=
        item.ordered && item.number !== 1
          ? `<ol start="${item.number}">`
          : `<${tag}>`;
      open.push(tag);
    }
    html += `<li>${item.body}`;
  }

  while (open.length > 0) html += `</li></${open.pop() ?? 'ul'}>`;
  return html;
}

/**
 * Turn every markdown mark the composer can write into tags at render time, the
 * same way `tagUrlsForRender` turns bare URLs into `<url>`: bold, italic,
 * strikethrough, code, links, headings, quotes and lists.
 *
 * Render-time, not write-time, on purpose: the backticks and asterisks stay in
 * the stored content, so editing a post gives the author back exactly what they
 * typed and re-saving re-derives the tags. `<` and `>` inside a code span are
 * escaped so a pasted snippet renders as text rather than becoming markup.
 *
 * Order is load-bearing, and each step says why it sits where it does.
 */
export const tagMarkdownForRender = (
  content: string,
  id: string,
  options?: {
    /**
     * The content is a chat message: typed text plus the tags `contentFilter`
     * wrote. Three things differ from a post. A typed `<` or `&` is text, so
     * a message cannot carry markup of its own (an image, a table, a class).
     * A `# heading` is a bold line, not a heading. And `+ ` does not start a
     * list.
     */
    chat?: boolean;
  }
): string => {
  if (!content) return content;
  const chat = options?.chat ?? false;

  // 1. Code out of the way first, so no rule below can reach inside a sample.
  const { shielded: codeShielded, blocks } = shieldCode(content);

  // 1a. Autolinks `<https://…>` in rows saved before contentFilter unwrapped
  // them are links, not unknown tags for the sanitiser to drop. Not in chat,
  // where a typed `<` is text and a message has always shown one as typed.
  const autolinked = chat
    ? codeShielded
    : codeShielded.replace(AUTOLINK, `<url id=${id[0]}>$1</url>`);

  // 1b. In chat, what was typed is text. Everything but the written tags is
  // escaped here, before any rule below adds a tag of its own.
  const shielded = chat ? escapeTypedMarkup(autolinked) : autolinked;

  // 2. Markdown links become anchors before anything else can touch the URL —
  // `nofollow ugc` matches the <url> tag's policy, since this is a user link.
  const linked = shielded.replace(
    MD_LINK,
    `<a href="$2" target="_blank" rel="noopener noreferrer nofollow ugc">$1</a>`
  );

  // 3. Entity tags out of the way, so `a_b_c` inside a link is not italicised.
  const { shielded: tagShielded, tags } = shieldTags(linked);

  // 4. Inline marks, then blocks: a `**bold**` list item is already a tag by
  // the time the line is scanned, so the line grammar sees a clean prefix.
  const marked = tagInlineMarks(tagShielded, id);
  const blocked = tagBlocksForRender(unshieldTags(marked, tags), id, chat);

  const wrap = (code: string) =>
    `<code id=${id[0]}>${code
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')}</code>`;

  // 5. Code last, escaped, so a pasted snippet renders as text. In chat a
  // fenced block that spans lines is a block on screen, so the line break the
  // sender typed on either side of the fence goes with it: kept, each would
  // show as an empty line above and below the block.
  const fence = chat ? CHAT_FENCED_CODE : FENCED_CODE;
  return unshieldCode(blocked, blocks)
    .replace(fence, (match) => {
      const fenced = match.replace(/^\n|\n$/g, '');
      // The word after the opening fence is a language only when a line
      // break follows it: in a one-line "```x```" it is the code.
      const code = fenced
        .replace(/^```(?:[a-zA-Z0-9+#-]*\n)?/, '')
        .replace(/```$/, '');
      if (!chat || code.includes('\n')) return wrap(code);
      // One line: an inline span, and the line breaks around it stay.
      return match.replace(fenced, () => wrap(code));
    })
    .replace(INLINE_CODE, (match) => wrap(match.slice(1, -1)));
};

export const stripContentTags = (content: string): string => {
  // An autolink is the URL the author typed, not a tag to drop.
  return unwrapAutolinks(content).replace(/<\/?[^>]+>/gi, '');
};

/**
 * Escape what a person typed in a message so it renders as text, leaving the
 * tags `contentFilter` wrote and the system bot's bare `<b>`, `<strong>` and
 * `<br/>`. `>` stays: it cannot open a tag, and at the start of a line it is
 * the quote mark.
 */
function escapeTypedMarkup(text: string): string {
  const escape = (typed: string) =>
    typed.replace(/&/g, '&amp;').replace(/</g, '&lt;');
  let escaped = '';
  let from = 0;
  for (const written of Array.from(text.matchAll(KEPT_IN_CHAT))) {
    const at = written.index ?? from;
    escaped += escape(text.slice(from, at)) + written[0];
    from = at + written[0].length;
  }
  return escaped + escape(text.slice(from));
}

/**
 * A message as its sender typed it: the stored content without the tags
 * `contentFilter` wrote. Only those go, so a typed `<div>` or `a < b` comes
 * back whole. For the edit box and for copying a message.
 */
export const messageSourceText = (content: string): string =>
  content.replace(WRITTEN_TAG, '');

/**
 * A message as one line of plain text, for every place that shows it outside
 * its bubble: the conversation list, a push, a search hit, a notification.
 * It is the text the bubble shows, because it is made from the same tagging
 * the bubble renders (`tagMarkdownForRender` with `chat`): marks, fences and
 * list markers are gone and no tag is left. `censor` writes `***` for a
 * censored word, for text that leaves the app.
 */
export const messagePlainText = (
  content: string,
  options?: { censor?: boolean }
): string => {
  if (!content) return '';
  return (
    tagMarkdownForRender(content, '', { chat: true })
      .replace(/<c id=[^>]*>([^<]*)<\/c>/g, (_tag, word: string) =>
        options?.censor ? '***' : word
      )
      // The edge of a block is a word boundary.
      .replace(/<\/?(?:ul|ol|li|blockquote|code)\b[^>]*>|<br ?\/>/g, ' ')
      // In chat mode every `<` still here opens a tag this file wrote.
      .replace(/<[^>]*>/g, '')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ')
      .trim()
  );
};

export const censorForNotification = (content: string): string => {
  return unwrapAutolinks(content)
    .replace(/<c id=[^>]+>[^<]+<\/c>/gi, '***')
    .replace(/<\/?[^>]+>/gi, '');
};

// Convert a word pattern to regex, handling wildcards (* for any chars)
function wordToRegex(word: string): string {
  // Check for wildcard patterns
  const hasLeadingWildcard = word.startsWith('*');
  const hasTrailingWildcard = word.endsWith('*');

  // Remove wildcards and escape special regex chars (except *)
  const pattern = word
    .replace(/^\*/, '')
    .replace(/\*$/, '')
    .replace(/[.+?^${}()|[\]\\]/g, '\\$&');

  // Build the pattern based on wildcards
  if (hasLeadingWildcard && hasTrailingWildcard) {
    // *word* - match anywhere in a word
    return `\\w*${pattern}\\w*`;
  } else if (hasLeadingWildcard) {
    // *word - match at end of word
    return `\\w*${pattern}\\b`;
  } else if (hasTrailingWildcard) {
    // word* - match at start of word
    return `\\b${pattern}\\w*`;
  } else {
    // exact word with word boundaries
    return `\\b${pattern}\\b`;
  }
}

// Check if a matched word should be excluded (is a legitimate word)
function isExcludedWord(match: string): boolean {
  const lowerMatch = match.toLowerCase();
  return profaneWordsList.excluded.some(
    (excluded) => excluded.toLowerCase() === lowerMatch
  );
}

export const cussWordFilter = (text: string, id: string) => {
  const profaneWordsRegexPart = profaneWordsList.words
    .filter((word) => !profaneWordsList.excluded.includes(word.toLowerCase()))
    .map((word) => wordToRegex(word))
    .join('|');

  const profaneRegex = new RegExp(`(${profaneWordsRegexPart})`, 'gi');

  return text.replace(profaneRegex, (match, _group, offset) => {
    // Check if this word is in the excluded list (legitimate words like "classic", "hello")
    if (isExcludedWord(match)) {
      return match;
    }
    if (isInsideTag(text, offset, 'c')) {
      return match;
    }
    return `<c id=${id[0]}>${match}</c>`;
  });
};

export const uncensorWord = (text: string, wordToUncensor: string): string => {
  // Remove <c> tags wrapping the specified word (case-insensitive)
  const escapedWord = wordToUncensor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const uncensorRegex = new RegExp(`<c id=[^>]+>(${escapedWord})<\\/c>`, 'gi');
  return text.replace(uncensorRegex, '$1');
};

export const uncensorAllFalsePositives = (text: string): string => {
  // Remove <c> tags from all words in the excluded list
  let result = text;
  for (const excludedWord of profaneWordsList.excluded) {
    result = uncensorWord(result, excludedWord);
  }
  return result;
};
